import { InspectionMethod } from '@library/dto-enums/inspection-method.dto-enum';
import { InspectionStatus } from '@library/dto-enums/inspection-status.dto-enum';
import { EQpHttpStatusCode } from '@library/models/qp-http.models';
import { EQpInspectionTypeLabel } from '@library/models/qp-inspection.models';
import { EQpProfile } from '@library/models/qp-profile.models';
import { NetworkStatusService } from '@library/services/qp-network-status/qp-network-status.service';
import { QpQueryParamsService } from '@library/services/qp-query-params/qp-query-params.service';
import { SERVER_API_URL } from '@one/app/app.constants';
import { IEntity } from '@one/app/pages/brd/shared/models/entity.model';
import {
  IESInspectionConsultation,
  IESInspectionConsultationProduct,
} from '@one/app/pages/brd/shared/models/es-inspection-consultation.model';
import { pdrFieldsFromInspectionConsultation } from '@one/app/pages/isp/pages/home/inspection-summary-pdr-fields.util';
import {
  EPlannedDateType,
  IInspectionsSummary,
  IInspectionSummary,
  IInspectionSummaryProduct,
} from '@one/app/pages/isp/pages/home/isp-home.models';
import {
  IProduceInspectionProductDTO,
  IProduceInspectionSearchDTO,
  ProduceInspectionStatusDTO,
  ProduceInspectionType,
} from '@one/app/pages/isp/pages/home/isp-produce-inspection.models';
import { INTERCEPTOR_SKIP_HEADER } from '@one/app/shared/interceptors/inspection.interceptor';
import {
  IInspectionConsultation,
  IInspectionStatisticsByStatusesDTO,
} from '@one/app/shared/models/inspection-consultation/inspection-consultation.models';
import { IAbstractMultiProduct } from '@one/app/shared/models/products/product.models';
import { AccountService } from '@one/app/shared/services/account/account.service';
import { DatabaseDataService } from '@one/app/shared/services/database/database-data.service';
import { DatabaseService } from '@one/app/shared/services/database/database-inspection.service';
import {
  IOfflineReportGroup,
  OfflineReportGroupReferenceType,
  OfflineReportGroupStatus,
} from '@one/app/shared/services/database/database-report-group.models';
import { mapInspectorsToParticipants } from '@one/app/shared/utils/multi-inspector.utils';
import { HttpHandlerFn, HttpRequest, HttpEvent, HttpResponse, HttpHeaders } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, from } from 'rxjs';
import { map } from 'rxjs/operators';

const GET_INSPECTIONS_SEARCH = RegExp(`^${SERVER_API_URL}api/inspections/search$`);
const GET_INSPECTIONS_SUMMARY = RegExp(`^${SERVER_API_URL}api/inspections/summary$`);
const GET_INSPECTORS_STATISTICS = RegExp(`^${SERVER_API_URL}api/inspectors/statistics$`);
const GET_PRODUCE_INSPECTIONS_SEARCH = RegExp(`^${SERVER_API_URL}api/produce-inspections/search$`);
const OFFLINE_REPORT_GROUP_TYPES: readonly OfflineReportGroupReferenceType[] = ['LOT', 'PACKING_LIST'];
const OFFLINE_REPORT_GROUP_STATUSES: readonly OfflineReportGroupStatus[] = ['IN_PROGRESS', 'COMPLETED'];

/**
 * @param  {IAbstractMultiProduct[]} products list to convert
 * @returns {IESInspectionConsultationProduct[]} the converted list ✨
 */
function convertIAbstractMultiProductToIESInspectionConsultationProduct(
  products: IAbstractMultiProduct[]
): IESInspectionConsultationProduct[] {
  return products.map((p): IESInspectionConsultationProduct => {
    return {
      purchaseOrderReference: p.purchaseOrderReference,
      identifierValue: p.identifierValue,
      name: p.specification['NAME'] ?? '',
      reference: p.specification['REFERENCE'] ?? '',
      shipmentDate: p.shipmentDate,
      brandName: '',
      id: p.productId,
      identifierType: p.identifierType,
      purchaseOrderId: p.purchaseOrderId,
      quantity: p.productQuantity,
      unit: p.unit,
    };
  });
}

/**
 * @param {IAbstractMultiProduct[]} products list to convert
 * @returns {IInspectionSummaryProduct[]} the converted list ✨
 */
function convertIAbstractMultiProductToIInspectionSummaryProduct(products: IAbstractMultiProduct[]): IInspectionSummaryProduct[] {
  return products.map((p): IInspectionSummaryProduct => {
    return {
      productIdentifierValue: p.identifierValue,
      productQuantity: p.productQuantity,
      purchaseOrderReference: p.purchaseOrderReference ?? '',
    };
  });
}

/**
 * @param  {IInspectionConsultation[]} inspections list of inspections to convert
 * @returns {IESInspectionConsultation[]} the converted list ✨
 */
function convertIInspectionConsultationToIESInspectionConsultation(inspections: IInspectionConsultation[]): IESInspectionConsultation[] {
  return inspections.map((i): IESInspectionConsultation => {
    const entity: IEntity | undefined = i.entity ?? i.responsibleEntity;
    const pdrFields = pdrFieldsFromInspectionConsultation(i);

    return {
      id: i.id!,
      inspectionUuid: i.inspectionUuid,
      createdOffline: i.createdOffline,
      version: 0,
      typeLabel: i.inspectionType.label as EQpInspectionTypeLabel,
      plannedDate: i.plannedDate,
      inspectionDate: i.plannedDate,
      createdDate: i.plannedDate,
      shipmentDate: i.purchaseOrder?.shipmentDate ?? '',
      status: i.status,
      method: i.method! as InspectionMethod,
      inspectorFullName: `${i.inspector?.firstName} ${i.inspector?.lastName}`,
      inspectorCompanyId: i.inspector?.brandId?.toString() ?? '',
      inspectorCompanyName: i.inspector?.companyName ?? '',
      inspectorId: i.inspector?.id ?? 0,
      entityId: entity?.id ?? 0,
      entityName: entity?.name ?? '',
      productId: i.products[0]?.productId ?? 0,
      productIdentifierValue: i.products[0]?.identifierValue ?? '',
      productQuantity: i.products[0]?.productQuantity ?? 0,
      unit: i.products[0]?.unit ?? '',
      brandName: i.brand?.name ?? '',
      brandId: i.brand?.id ?? 0,
      entity,
      entityCountry: entity?.address.country ?? '',
      entityCity: entity?.address.city ?? '',
      entityCustomId: pdrFields.entityCustomId,
      entityLatitude: pdrFields.entityLatitude,
      entityLongitude: pdrFields.entityLongitude,
      plannedStartTime: pdrFields.plannedStartTime,
      plannedDurationHours: pdrFields.plannedDurationHours,
      result: i.result,
      products: convertIAbstractMultiProductToIESInspectionConsultationProduct(i.products),
      inspectors: mapInspectorsToParticipants(i.inspectors),
    } as unknown as IESInspectionConsultation;
  });
}

/**
 * @param filterParam
 */
function _parseInspectionStatuses(filterParam: string): InspectionStatus[] | undefined {
  if (filterParam.includes('status,EQUALS,')) {
    const inspectionStatus = InspectionStatus[filterParam.split('status,EQUALS,')[1]];

    return [inspectionStatus];
  }

  if (filterParam.includes('status,IN,')) {
    const inspectionStatuses = filterParam.replaceAll('[', '').replaceAll(']', '').split('status,IN,')[1].split(',');

    return inspectionStatuses.map((status): InspectionStatus => InspectionStatus[status]);
  }

  return [];
}

/**
 * @param {IInspectionConsultation[]} inspections list of inspections to convert
 * @returns {IInspectionsSummary[]} the converted list ✨
 */
function convertIInspectionConsultationToIInspectionsSummary(inspections: IInspectionConsultation[]): IInspectionsSummary[] {
  const inspectionsSummary: IInspectionsSummary[] = [
    { type: EPlannedDateType.TODAY, inspections: [] },
    { type: EPlannedDateType.FUTURE, inspections: [] },
  ];

  inspections.forEach((inspection): void => {
    const inspectionSummary: IInspectionSummary = convertIInspectionConsultationToIInspectionSummary(inspection);
    const plannedDate: Date = new Date(inspection.plannedDate);
    const today: string = new Date().toISOString().split('T')[0];
    const plannedDateString: string = plannedDate.toISOString().split('T')[0];

    if (plannedDateString < today) {
      return;
    }

    inspectionsSummary
      .find((summary): boolean => summary.type === (plannedDateString === today ? EPlannedDateType.TODAY : EPlannedDateType.FUTURE))
      ?.inspections.push(inspectionSummary);
  });

  return inspectionsSummary;
}

/**
 * @param {IInspectionConsultation} inspection the inspection to convert
 * @returns {IInspectionSummary} the converted inspection ✨
 */
function convertIInspectionConsultationToIInspectionSummary(inspection: IInspectionConsultation): IInspectionSummary {
  const entity: IEntity | undefined = inspection.entity ?? inspection.responsibleEntity;
  const pdrFields = pdrFieldsFromInspectionConsultation(inspection);

  return {
    id: inspection.id!,
    inspectionUuid: inspection.inspectionUuid,
    createdOffline: inspection.createdOffline,
    brandName: inspection.brand?.name ?? '',
    entityName: entity?.name ?? '',
    country: entity?.address.country.label ?? '',
    city: entity?.address.city ?? '',
    type: inspection.inspectionType,
    date: inspection.plannedDate,
    status: inspection.status,
    inspectedProducts: convertIAbstractMultiProductToIInspectionSummaryProduct(inspection.products),
    result: inspection.result,
    ratingResults: inspection.ratingResults,
    participants: mapInspectorsToParticipants(inspection.inspectors),
    entityCustomId: pdrFields.entityCustomId,
    entityLatitude: pdrFields.entityLatitude,
    entityLongitude: pdrFields.entityLongitude,
    plannedStartTime: pdrFields.plannedStartTime,
    plannedDurationHours: pdrFields.plannedDurationHours,
  };
}

/**
 * @param {IInspectionConsultation[]} inspections list of inspections to convert
 * @returns {IInspectionStatisticsByStatusesDTO[]} the converted list ✨
 */
function convertIInspectionConsultationToIInspectionStatisticsByStatusesDTO(
  inspections: IInspectionConsultation[]
): IInspectionStatisticsByStatusesDTO {
  const statistics: IInspectionStatisticsByStatusesDTO = {
    inspectionsByStatuses: {},
  };

  inspections.forEach((inspection): void => {
    statistics.inspectionsByStatuses[inspection.status] = (statistics.inspectionsByStatuses[inspection.status] ?? 0) + 1;
  });

  return statistics;
}

/**
 * @param {IInspectionConsultation} inspection the inspection to check
 * @returns {boolean} true if the inspection is allowed to be displayed offline, false otherwise ✨
 * The inspection is allowed to be displayed offline if it is created offline and has an inspection uuid, or if it
 * is ACCEPTED or STARTED. Both statuses are preloaded to the local database for every invited multi-inspector
 * co-inspector (see IspInspectionPreloadService#preloadInspectionForInvitee), not only STARTED ones.
 */
function isInspectionAllowedToBeDisplayedOffline(inspection: IInspectionConsultation): boolean {
  return (
    (!!inspection.createdOffline && !!inspection.inspectionUuid) ||
    inspection.status === InspectionStatus.STARTED ||
    inspection.status === InspectionStatus.ACCEPTED
  );
}

/**
 * Allow on the offline dashboard any locally cached report group that owns a {@code groupReportUuid}.
 * Mirrors {@link isInspectionAllowedToBeDisplayedOffline} for single-sample inspections.
 * @param {IOfflineReportGroup} group the group to check
 * @returns {boolean} true if the group should appear in the offline dashboard
 */
function isOfflineReportGroupAllowedToBeDisplayedOffline(group: IOfflineReportGroup): boolean {
  return !!group.groupReportUuid;
}

/**
 * Reads a repeated query param (e.g. {@code ?type=LOT&type=PACKING_LIST}) parsed by query-string into the
 * known enum values. {@code parseUrl} returns either {@code undefined}, a single primitive, or an array
 * depending on the request shape; this helper normalizes all three.
 * @param raw the raw value coming out of {@link QpQueryParamsService.getQueryParams}
 * @param allowed the set of values that are valid for this param
 * @returns the parsed allowed values, or an empty array when nothing matched
 */
function _parseRepeatedEnumQueryParam<T extends string>(
  raw: string | number | boolean | null | (string | number | boolean | null)[] | undefined,
  allowed: readonly T[]
): T[] {
  if (raw === undefined || raw === null) {
    return [];
  }

  const allowedSet = new Set<string>(allowed);
  const values = Array.isArray(raw) ? raw : [raw];

  return values.map((value): string => String(value)).filter((value: string): value is T => allowedSet.has(value)) as T[];
}

/**
 * Converts a locally cached offline report group into the same row shape served by
 * {@code GET /api/produce-inspections/search}, so it can be merged into the unified inspector dashboard
 * without any UI-side branching. The numeric {@code id} stays {@code 0} until the queued
 * {@code POST /from-reference} action syncs and {@code remapOfflineReportGroupId} populates it.
 * Navigation works because the dashboard card mapper now keys on {@code inspectionUuid}
 * (the {@code groupReportUuid}) when {@code createdOffline} is set.
 * @param group offline group cache row
 * @returns the dashboard search row shape
 */
function convertOfflineReportGroupToProduceInspectionSearchDTO(group: IOfflineReportGroup): IProduceInspectionSearchDTO {
  const products: IProduceInspectionProductDTO[] = group.productIdentifiers.map(
    (value): IProduceInspectionProductDTO => ({
      productIdentifierValue: value,
      quantity: null,
      purchaseOrderReference: null,
    })
  );

  return {
    id: group.reportGroupId ?? 0,
    type: group.referenceType,
    status: group.status,
    date: group.date,
    createdDate: group.createdDate,
    reference: group.reference.length > 0 ? group.reference : null,
    products,
    inspectionSiteName: group.inspectionSiteName.length > 0 ? group.inspectionSiteName : null,
    lotReferences: [],
    packingListReference: group.shell.packingListReference ?? null,
    inspectionUuid: group.groupReportUuid,
    createdOffline: group.reportGroupId == null,
    brandName: group.shell.brand?.name ?? null,
    entityName: null,
    entityCity: null,
    entityCountry: null,
    typeLabel: null,
    inspectionStatus: null,
    result: null,
    ratingResults: null,
  };
}

/**
 * @param {HttpRequest} request the request to intercept
 * @param {HttpHandlerFn} next the request handler
 * @returns {Observable<HttpEvent<unknown>> | Observable<HttpResponse<undefined>>} the request or the response to return
 */
export function inspectorHomeInterceptor$(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> | Observable<HttpResponse<undefined>> {
  const databaseService: DatabaseService = inject(DatabaseService);
  const databaseDataService: DatabaseDataService = inject(DatabaseDataService);
  const accountService: AccountService = inject(AccountService);
  const networkStatusService: NetworkStatusService = inject(NetworkStatusService);
  const queryParamsService: QpQueryParamsService = inject(QpQueryParamsService);

  if (
    !accountService.hasAnyAuthority([EQpProfile.ROLE_SERVICE_PROVIDER_INSPECTOR, EQpProfile.ROLE_INSPECTOR]) ||
    request.headers.has(INTERCEPTOR_SKIP_HEADER) ||
    networkStatusService.isOnline
  ) {
    return next(request);
  }

  const pathname: string = queryParamsService.getPathname(request.url);

  // 🔗 GET: /api/inspections/search&filter=status,EQUALS,PLANNED
  if (request.method === 'GET' && GET_INSPECTIONS_SEARCH.test(pathname)) {
    const filterParam: string = queryParamsService.getQueryParams(request.urlWithParams)['filter']?.toString() ?? '';
    const statuses: InspectionStatus[] | undefined = _parseInspectionStatuses(filterParam);

    if (!statuses) {
      return next(request);
    }

    return from(databaseService.getInspections()).pipe(
      map((resp): HttpResponse<IESInspectionConsultation[]> => {
        let inspections: IInspectionConsultation[] = resp;

        inspections = inspections
          .filter((inspection): boolean => statuses.includes(inspection.status))
          .filter((inspection): boolean => isInspectionAllowedToBeDisplayedOffline(inspection))
          .sort((inspectionA, inspectionB): number => {
            if (inspectionA.inspectionDate && inspectionB.inspectionDate) {
              return new Date(inspectionB.inspectionDate).getTime() - new Date(inspectionA.inspectionDate).getTime();
            }

            return 0;
          });

        const body: IESInspectionConsultation[] = convertIInspectionConsultationToIESInspectionConsultation(inspections);

        return new HttpResponse({
          status: EQpHttpStatusCode.OK,
          body,
          headers: new HttpHeaders().append('x-total-count', body.length.toString()).append('x-total-page', '1'),
        });
      })
    );
  }

  // 🔗 GET: /api/inspections/summary
  if (request.method === 'GET' && GET_INSPECTIONS_SUMMARY.test(pathname)) {
    return from(databaseService.getInspections()).pipe(
      map((resp): HttpResponse<IInspectionsSummary[]> => {
        return new HttpResponse({
          status: EQpHttpStatusCode.OK,
          body: convertIInspectionConsultationToIInspectionsSummary(resp),
        });
      })
    );
  }

  // 🔗 GET: /api/inspectors/statistics
  if (request.method === 'GET' && GET_INSPECTORS_STATISTICS.test(pathname)) {
    return from(databaseService.getInspections()).pipe(
      map((resp): HttpResponse<IInspectionStatisticsByStatusesDTO> => {
        return new HttpResponse({
          status: EQpHttpStatusCode.OK,
          body: convertIInspectionConsultationToIInspectionStatisticsByStatusesDTO(resp),
        });
      })
    );
  }

  // 🔗 GET: /api/produce-inspections/search?type=LOT&status=IN_PROGRESS
  // Surfaces offline-created group inspections (lot / packing-list) on the inspector home dashboard.
  // The inspector home component only ever calls this endpoint with `type ∈ ['LOT','PACKING_LIST']`
  // (see DASHBOARD_GROUP_TYPES in IspHomeComponent), so returning only group rows here cannot create
  // duplicate sample cards alongside the /api/inspections/search branch above.
  if (request.method === 'GET' && GET_PRODUCE_INSPECTIONS_SEARCH.test(pathname)) {
    const queryParams = queryParamsService.getQueryParams(request.urlWithParams);
    const requestedTypes: ProduceInspectionType[] = _parseRepeatedEnumQueryParam(queryParams['type'], OFFLINE_REPORT_GROUP_TYPES);
    const requestedStatuses: ProduceInspectionStatusDTO[] = _parseRepeatedEnumQueryParam(
      queryParams['status'],
      OFFLINE_REPORT_GROUP_STATUSES
    );

    return from(databaseDataService.getOfflineReportGroups()).pipe(
      map((groups: IOfflineReportGroup[]): HttpResponse<IProduceInspectionSearchDTO[]> => {
        const filtered: IOfflineReportGroup[] = groups
          .filter(isOfflineReportGroupAllowedToBeDisplayedOffline)
          .filter((group): boolean => requestedTypes.length === 0 || requestedTypes.includes(group.referenceType))
          .filter((group): boolean => requestedStatuses.length === 0 || requestedStatuses.includes(group.status))
          .sort((groupA, groupB): number => new Date(groupB.createdDate).getTime() - new Date(groupA.createdDate).getTime());
        const body: IProduceInspectionSearchDTO[] = filtered.map(convertOfflineReportGroupToProduceInspectionSearchDTO);

        return new HttpResponse({
          status: EQpHttpStatusCode.OK,
          body,
          headers: new HttpHeaders().append('x-total-count', body.length.toString()).append('x-total-page', '1'),
        });
      })
    );
  }

  return next(request);
}
